import { css, CSSObject } from '@emotion/react';
import { DeleteOutline, ExpandMore } from '@mui/icons-material';
import { Button, IconButton, InputBase, Stack, Typography } from '@mui/material';
import { MouseEvent, useMemo, useState } from 'react';
import { IField } from '../../../../data-models/field2.data-model';
import { colors } from '../../../../theme/colors';

interface KpiConfigSelectorProps {
  availableKpis: IField<unknown>[];
  selectedKpiIds: number[];
  onSelect: (kpiId: number) => void;
  onCreateNew?: (kpiName?: string) => void;
  /** Optional muted right-side label per option (e.g. "custom" / "universal"). */
  getOptionSecondaryLabel?: (kpi: IField<unknown>) => string | undefined;
  /** When provided, a trash icon is shown for options where `isDeletable` returns true. */
  onDeleteOption?: (kpiId: number) => void;
  /** Defaults to `() => true` when `onDeleteOption` is provided. */
  isDeletable?: (kpi: IField<unknown>) => boolean;
}

const triggerCss = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0.5rem 0.75rem',
  background: 'transparent',
  border: `1px solid ${colors.neutral[20]}`,
  borderRadius: '4px',
  cursor: 'pointer',
  font: 'inherit',
  color: colors.neutral[60],
  textAlign: 'left',
  '&:hover': {
    backgroundColor: 'var(--mui-palette-action-hover)',
  },
});

const chevronCss = (expanded: boolean) =>
  css({
    transition: 'transform 200ms ease',
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  });

const panelCss = css({
  marginTop: '0.5rem',
  border: `1px solid ${colors.neutral[20]}`,
  borderRadius: '4px',
  background: colors.primary[0],
  overflow: 'hidden',
});

const searchCss = css({
  padding: '0.5rem 0.75rem',
  borderBottom: `1px solid ${colors.neutral[10]}`,
  width: '100%',
});

const listCss = css({
  maxHeight: '240px',
  overflowY: 'auto',
});

const optionRowCss = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  '&:hover': {
    backgroundColor: 'var(--mui-palette-action-hover)',
  },
  '&:focus-within': {
    backgroundColor: 'var(--mui-palette-action-hover)',
  },
});

const optionBase: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flex: 1,
  minWidth: 0,
  padding: '0.5rem 0.75rem',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'left',
};

const optionCss = css(optionBase);

const optionLabelCss = css({ flex: 1, minWidth: 0 });

const deleteIconCss = css({ marginRight: '0.25rem' });

const secondaryLabelCss = css({
  color: colors.neutral[50],
  fontSize: '0.75rem',
});

const emptyCss = css({
  padding: '1rem 0.75rem',
  color: colors.neutral[50],
  textAlign: 'center',
});

const footerCss = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0.75rem',
  borderTop: `1px solid ${colors.neutral[10]}`,
  background: colors.neutral[5],
});

const createCss = css({
  ...optionBase,
  borderTop: `1px solid ${colors.neutral[10]}`,
  color: colors.primary[50],
  fontWeight: 500,
});

export function KpiConfigSelector({
  availableKpis,
  selectedKpiIds,
  onSelect,
  onCreateNew,
  getOptionSecondaryLabel,
  onDeleteOption,
  isDeletable,
}: KpiConfigSelectorProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(() => new Set(selectedKpiIds), [selectedKpiIds]);

  const remaining = useMemo(
    () => availableKpis.filter((kpi) => !selectedSet.has(kpi.id)),
    [availableKpis, selectedSet]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return remaining;
    }
    return remaining.filter((kpi) => kpi.displayName.toLowerCase().includes(q));
  }, [remaining, search]);

  const hasContent = remaining.length > 0 || !!onCreateNew;
  if (!hasContent) {
    return null;
  }

  return (
    <Stack>
      <button
        type='button'
        css={triggerCss}
        onClick={() => {
          if (expanded) {
            setSearch('');
          }
          setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
      >
        <Typography variant='body2' color='inherit'>
          Add KPI
        </Typography>
        <ExpandMore css={chevronCss(expanded)} fontSize='small' />
      </button>

      {expanded && (
        <div css={panelCss}>
          <InputBase
            css={searchCss}
            placeholder='Search KPIs...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          <div css={listCss}>
            {filtered.length === 0 ? (
              <div css={emptyCss}>
                <Typography variant='body2' color='inherit'>
                  {remaining.length === 0 ? 'All KPIs added' : 'No KPIs match'}
                </Typography>
              </div>
            ) : (
              filtered.map((kpi) => {
                const secondaryLabel = getOptionSecondaryLabel?.(kpi);
                const canDelete = !!onDeleteOption && (isDeletable ? isDeletable(kpi) : true);
                const handleDeleteClick = (e: MouseEvent) => {
                  e.stopPropagation();
                  onDeleteOption?.(kpi.id);
                };
                return (
                  <div key={kpi.id} css={optionRowCss}>
                    <button
                      type='button'
                      css={optionCss}
                      onClick={() => {
                        onSelect(kpi.id);
                        setSearch('');
                      }}
                    >
                      <Typography variant='body2' css={optionLabelCss}>
                        {kpi.displayName}
                      </Typography>
                      {secondaryLabel && (
                        <Typography variant='caption' css={secondaryLabelCss}>
                          {secondaryLabel}
                        </Typography>
                      )}
                    </button>
                    {canDelete && (
                      <IconButton
                        size='small'
                        color='secondary'
                        onClick={handleDeleteClick}
                        aria-label={`Delete ${kpi.displayName}`}
                        css={deleteIconCss}
                      >
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    )}
                  </div>
                );
              })
            )}

            {onCreateNew && (
              <button type='button' css={createCss} onClick={() => onCreateNew(search.trim() || undefined)}>
                <Typography variant='body2' color='inherit'>
                  + Create New KPI
                </Typography>
              </button>
            )}
          </div>

          <div css={footerCss}>
            <Typography variant='caption' color={colors.neutral[60]}>
              {selectedKpiIds.length} selected · {remaining.length} remaining
            </Typography>
            <Button
              size='small'
              onClick={() => {
                setExpanded(false);
                setSearch('');
              }}
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </Stack>
  );
}
